import React from 'react';
import {
    Box, Chip, CircularProgress, Divider, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import { type Box as BoxInfo, VOLUME_FULLNESS_THRESHOLDS } from '../../config/infra';
import {
    type VolumesStatus, formatBytes, isDegraded, usagePercent, fullnessColor,
} from '../../api/volumesApi';

interface Props {
    box: BoxInfo;
    /** undefined = still loading first result */
    status: VolumesStatus | undefined;
}

function reachabilityChip(status: VolumesStatus | undefined) {
    if (status === undefined) {
        return <Chip size='small' icon={<CircularProgress size={12} />} label='checking…' variant='outlined' />;
    }
    if (!status.reachable) {
        return <Chip size='small' color='error' label={`unreachable${status.error ? ` — ${status.error}` : ''}`} />;
    }
    const when = status.reportedAt ? new Date(status.reportedAt).toLocaleTimeString() : undefined;
    return <Chip size='small' color='success' label={when ? `live · ${when}` : 'live'} />;
}

/** Compact bar-plus-label used for every volume/mount fullness indicator. */
const FullnessBar: React.FC<{ pct: number | undefined }> = ({ pct }) => {
    if (pct === undefined) return null;
    return (
        <LinearProgress
            variant='determinate'
            value={pct}
            sx={{
                height: 6, borderRadius: 3, mt: 0.25,
                backgroundColor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': { backgroundColor: fullnessColor(pct, VOLUME_FULLNESS_THRESHOLDS) },
            }}
        />
    );
};

/** Worst (highest-fullness) reading across everything this card can show, for the header rollup chip. */
function worstFullness(status: VolumesStatus): number | undefined {
    const pcts: number[] = [];
    for (const lv of status.logicalVolumes) {
        const mount = status.mounts.find(m => m.source === lv.path);
        const pct = usagePercent(mount?.used_bytes, mount?.size_bytes ?? lv.size_bytes);
        if (pct !== undefined) pcts.push(pct);
    }
    for (const vg of status.volumeGroups) {
        const pct = usagePercent(vg.size_bytes - vg.free_bytes, vg.size_bytes);
        if (pct !== undefined) pcts.push(pct);
    }
    for (const m of status.mounts) {
        const pct = usagePercent(m.used_bytes, m.size_bytes);
        if (pct !== undefined) pcts.push(pct);
    }
    return pcts.length > 0 ? Math.max(...pcts) : undefined;
}

function worstFullnessChip(boxId: string, status: VolumesStatus) {
    const worst = worstFullness(status);
    if (worst === undefined) return null;
    const colour = fullnessColor(worst, VOLUME_FULLNESS_THRESHOLDS);
    const label = worst >= VOLUME_FULLNESS_THRESHOLDS.red
        ? `full · ${worst}%`
        : worst >= VOLUME_FULLNESS_THRESHOLDS.amber
            ? `filling · ${worst}%`
            : `${worst}% used`;
    return (
        <Chip
            size='small'
            data-testid={`worst-volume-${boxId}`}
            label={label}
            sx={{ backgroundColor: colour, color: '#111' }}
        />
    );
}

const DriveLayoutCard: React.FC<Props> = ({ box, status }) => (
    <Paper
        elevation={3}
        data-testid={`drives-${box.id}`}
        sx={{ p: 2, backgroundColor: '#1f2329', height: '100%', display: 'flex', flexDirection: 'column' }}
    >
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ mb: 1 }}>
            <Typography variant='h6'>{box.name}</Typography>
            <Stack direction='row' spacing={0.75} alignItems='center'>
                {status?.reachable && worstFullnessChip(box.id, status)}
                {reachabilityChip(status)}
            </Stack>
        </Stack>

        {status?.reachable && (
            <>
                {status.disks.length > 0 && (
                    <>
                        <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.5)' }}>Disks</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                            {status.disks.map(d => (
                                <Chip
                                    key={d.name}
                                    size='small'
                                    variant='outlined'
                                    data-testid={`disk-${box.id}-${d.name}`}
                                    label={`${d.name} · ${formatBytes(d.size_bytes)}${d.model ? ` · ${d.model}` : ''}`}
                                />
                            ))}
                        </Box>
                    </>
                )}

                {status.arrays.length > 0 && (
                    <>
                        <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.5)' }}>mdadm arrays</Typography>
                        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                            {status.arrays.map(a => {
                                const degraded = isDegraded(a);
                                return (
                                    <Stack
                                        key={a.device}
                                        direction='row'
                                        alignItems='center'
                                        spacing={1}
                                        data-testid={`array-${box.id}-${a.device}`}
                                        data-degraded={degraded}
                                    >
                                        <Chip
                                            size='small'
                                            label={`${a.device} ${a.level}`}
                                            color={degraded ? 'error' : 'success'}
                                        />
                                        <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                            [{a.devices}] [{a.slot_state}]
                                            {a.resync_action && a.resync_percent !== undefined
                                                ? ` · ${a.resync_action} ${a.resync_percent.toFixed(1)}%`
                                                : ''}
                                        </Typography>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </>
                )}

                {status.volumeGroups.length > 0 && (
                    <>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.5)' }}>Volume groups</Typography>
                        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                            {status.volumeGroups.map(vg => {
                                const used = vg.size_bytes - vg.free_bytes;
                                const pct = usagePercent(used, vg.size_bytes);
                                return (
                                    <Box key={vg.name} data-testid={`vg-${box.id}-${vg.name}`}>
                                        <Stack direction='row' justifyContent='space-between'>
                                            <Typography variant='body2'>{vg.name}</Typography>
                                            <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                {formatBytes(used)} / {formatBytes(vg.size_bytes)}
                                                {pct !== undefined ? ` (${pct}%)` : ''}
                                            </Typography>
                                        </Stack>
                                        <FullnessBar pct={pct} />
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}

                {status.logicalVolumes.length > 0 && (
                    <>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.5)' }}>Logical volumes</Typography>
                        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                            {status.logicalVolumes.map(lv => {
                                const mount = status.mounts.find(m => m.source === lv.path);
                                const pct = usagePercent(mount?.used_bytes, mount?.size_bytes ?? lv.size_bytes);
                                return (
                                    <Box key={`${lv.vg}/${lv.name}`} data-testid={`lv-${box.id}-${lv.name}`}>
                                        <Stack direction='row' justifyContent='space-between'>
                                            <Typography variant='body2'>
                                                {lv.vg}/{lv.name}
                                                {mount ? ` — ${mount.target}` : ''}
                                            </Typography>
                                            <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                {formatBytes(mount?.used_bytes)} / {formatBytes(lv.size_bytes)}
                                                {pct !== undefined ? ` (${pct}%)` : ''}
                                                {!lv.active ? ' · inactive' : ''}
                                            </Typography>
                                        </Stack>
                                        <FullnessBar pct={pct} />
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}

                {(() => {
                    // Completeness over prettiness (see project memory): every mount the
                    // box reports with usage data gets a bar, not just the ones backed by
                    // an LV — a plain root/ext4 mount on Cal is just as much "this box's
                    // storage" as an LVM logical volume on Luke/Leia.
                    const lvSources = new Set(status.logicalVolumes.map(lv => lv.path));
                    const otherMounts = status.mounts.filter(
                        m => m.size_bytes !== undefined && !(m.source && lvSources.has(m.source)),
                    );
                    if (otherMounts.length === 0) return null;
                    return (
                        <>
                            <Divider sx={{ mb: 1 }} />
                            <Typography variant='overline' sx={{ color: 'rgba(255,255,255,0.5)' }}>Mounts</Typography>
                            <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                                {otherMounts.map(m => {
                                    const pct = usagePercent(m.used_bytes, m.size_bytes);
                                    return (
                                        <Box key={m.target} data-testid={`mount-${box.id}-${m.target}`}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <Typography variant='body2'>{m.target}</Typography>
                                                <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                    {formatBytes(m.used_bytes)} / {formatBytes(m.size_bytes)}
                                                    {pct !== undefined ? ` (${pct}%)` : ''}
                                                </Typography>
                                            </Stack>
                                            <FullnessBar pct={pct} />
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </>
                    );
                })()}

                {status.disks.length === 0 && status.arrays.length === 0 && status.volumeGroups.length === 0
                    && status.logicalVolumes.length === 0
                    && status.mounts.every(m => m.size_bytes === undefined) && (
                    <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        No RAID/LVM layer on this box.
                    </Typography>
                )}
            </>
        )}

        <Box sx={{ flexGrow: 1 }} />
    </Paper>
);

export default DriveLayoutCard;
